/**
 * The dashboard's toasts: a short message top right (across the top on a
 * phone), below the view-as bar while it shows, over the page and its
 * dialogs, so nothing on the page moves. A toast says what the person cannot
 * see otherwise, never only that the action they just took worked: the page
 * shows that itself. Three kinds, through `toast.info`, `toast.success` and
 * `toast.error`:
 *
 * - info, a white card: something to know, such as a view that ended in
 *   another tab;
 * - success, soft green: a change went through, and what happens next;
 * - error, soft red: something was refused or failed.
 *
 * `<Toaster>` (`src/components/ui/toaster.tsx`) shows them, once, at the
 * root. A toast's text is an i18n key with its values, or a function that
 * words it, and is worded at render, so a language switch rewords a toast on
 * screen. Each closes by itself (info and success after 4 s, an error after
 * 6 s), later while the pointer or the focus is on the toasts, or at once with
 * its close button. Screen readers hear info and success when they are free,
 * an error at once. `dismissToasts` closes them all at once, for a control
 * that opens where they sit (the view-as bar's Wissel list).
 *
 * `toastAfterReload` keeps a toast for the next load of the page, for a
 * moment that reloads it (a view as someone else that ended or changed in
 * another tab).
 */

import { createElement } from "react";
import { toast as sonner } from "sonner";

import { ToastCard } from "@/components/toast-card";
import { i18n } from "@/lib/i18n";
import { logger } from "@/lib/logger";

export type ToastVariant = "info" | "success" | "error";

/** A text kept to be worded at render: an i18n key and its values. It survives a reload. */
export interface ToastKeyText {
  key: string;
  values?: Record<string, string>;
}

/** What a toast says: an i18n key and its values, or a function that words it at render. */
export type ToastText = ToastKeyText | (() => string);

export interface ToastOptions {
  /**
   * A toast with the same id as one on screen replaces it instead of adding a
   * second, and starts its time again: two refusals at once give one toast.
   */
  id?: string;
  /** How long it stays, in ms, when a toast needs longer than its kind's default. */
  durationMs?: number;
}

/** How long each kind stays, in ms: an error a little longer, as it asks to be read. */
export const TOAST_DURATION_MS: Record<ToastVariant, number> = {
  info: 4_000,
  success: 4_000,
  error: 6_000,
};

/** How urgently screen readers say each kind: an error at once, the others when they are free. */
export function toastPoliteness(variant: ToastVariant): "polite" | "assertive" {
  return variant === "error" ? "assertive" : "polite";
}

/** The live region the toasts appear in, once `<Toaster>` is mounted. */
let liveRegion: Pick<HTMLElement, "setAttribute"> | null = null;

/** `<Toaster>` hands over its live region, so each toast can set how urgently it is said. */
export function setToastRegion(region: Pick<HTMLElement, "setAttribute"> | null): void {
  liveRegion = region;
}

/** Counts every toast shown, so a toast that replaces one starts afresh. */
let shown = 0;

function showToast(variant: ToastVariant, text: ToastText, options: ToastOptions = {}) {
  // Before the toast reaches the region, so screen readers say it as urgently as its kind asks.
  liveRegion?.setAttribute("aria-live", toastPoliteness(variant));
  shown += 1;
  const key = shown;
  const durationMs = options.durationMs ?? TOAST_DURATION_MS[variant];
  logger.debug("[toast]", variant, typeof text === "function" ? "(worded at render)" : text.key);
  return sonner.custom(
    (id) =>
      createElement(ToastCard, {
        key,
        variant,
        text,
        durationMs,
        onDismiss: () => sonner.dismiss(id),
      }),
    // The card keeps its own time, so it can pause on focus as well as on hover.
    { id: options.id, duration: Infinity },
  );
}

export const toast = {
  info: (text: ToastText, options?: ToastOptions) => showToast("info", text, options),
  success: (text: ToastText, options?: ToastOptions) => showToast("success", text, options),
  error: (text: ToastText, options?: ToastOptions) => showToast("error", text, options),
};

/**
 * Closes every toast on screen, for a control that opens where they sit (top
 * right), so none covers what the person is about to use.
 */
export function dismissToasts(): void {
  logger.debug("[toast]", "dismiss all");
  sonner.dismiss();
}

/** Where a toast waits for the next load of the page, in this tab only. */
const STORAGE_KEY = "dc-toast-after-reload";

interface StoredToast {
  variant: ToastVariant;
  text: ToastKeyText;
}

/**
 * Keeps a toast for the next load of the page in this tab, for a moment that
 * reloads it: the page shows it once, then forgets it. Without storage (a
 * private window that refuses it) the toast is simply not shown.
 */
export function toastAfterReload(variant: ToastVariant, text: ToastKeyText): void {
  try {
    const stored: StoredToast = { variant, text };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    logger.warn("[toast] a toast for after the reload could not be stored", error);
  }
}

/** Shows the toast an earlier page kept for this load, once: it is removed as it is read. */
export function showToastAfterReload(): void {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
  const stored = raw === null ? null : parseStoredToast(raw);
  if (stored) {
    toast[stored.variant](stored.text);
  }
}

/** A stored toast, or null for anything that is not one: another version's, or a key that is gone. */
function parseStoredToast(raw: string): StoredToast | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  const { variant, text } = (value ?? {}) as Partial<StoredToast>;
  if (variant !== "info" && variant !== "success" && variant !== "error") {
    return null;
  }
  if (typeof text?.key !== "string" || !i18n.exists(text.key)) {
    return null;
  }
  const values = text.values ?? {};
  if (
    typeof values !== "object" ||
    Object.values(values).some((entry) => typeof entry !== "string")
  ) {
    return null;
  }
  return { variant, text: { key: text.key, values } };
}
